import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { styled, alpha } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import MuiToolbar from '@mui/material/Toolbar';
import { tabsClasses } from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import SideMenuMobile from './SideMenuMobile';
import { useTranslation } from 'react-i18next';
import { warmSuccessPrimary } from '../theme/warmSuccessDesignSystem';

const Toolbar = styled(MuiToolbar)({
  width: '100%',
  padding: '12px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'start',
  justifyContent: 'center',
  gap: '12px',
  flexShrink: 0,
  [`& ${tabsClasses.flexContainer}`]: {
    gap: '8px',
    p: '8px',
    pb: 0,
  },
});

export default function AppNavbar() {
  const [open, setOpen] = React.useState(false);
  const location = useLocation();
  const { t } = useTranslation('dashboard');

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  // Determine page title based on route
  const getPageTitle = () => {
    const path = location.pathname;
    if (path.includes('/dashboard/profile')) return t('pageTitle.profile');
    if (path.includes('/dashboard/applications')) return t('pageTitle.applications');
    if (path.includes('/dashboard/cv-builder')) return t('pageTitle.cvBuilder');
    if (path.includes('/dashboard/match-emploi')) return t('pageTitle.matchEmploi');
    if (path.includes('/dashboard/integrations')) return t('pageTitle.integrations');
    if (path.includes('/dashboard/stats')) return t('pageTitle.stats');
    if (path.includes('/dashboard/details')) return t('pageTitle.details');
    if (path.includes('/dashboard')) return t('pageTitle.default');
    return t('pageTitle.default');
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        display: { xs: 'auto', md: 'none' },
        boxShadow: 0,
        bgcolor: 'background.paper',
        backgroundImage: 'none',
        borderBottom: '1px solid',
        borderColor: 'divider',
        top: 'var(--template-frame-height, 0px)',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar variant="regular">
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            flexGrow: 1,
            width: '100%',
            gap: 1,
          }}
        >
          {/* Menu hamburger avec touch target 44x44px minimum */}
          <IconButton
            aria-label="menu"
            onClick={toggleDrawer(true)}
            sx={{
              minWidth: 44,
              minHeight: 44,
              borderRadius: 1,
              color: 'text.primary',
            }}
          >
            <MenuRoundedIcon />
          </IconButton>
          <Stack
            direction="row"
            spacing={1}
            sx={{ justifyContent: 'center', flexGrow: 1, alignItems: 'center' }}
          >
            <CustomIcon />
            <Typography
              variant="h6"
              component="h1"
              sx={{
                color: 'text.primary',
                fontSize: { xs: '1rem', sm: '1.25rem' },
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: { xs: '180px', sm: '300px' },
              }}
            >
              {getPageTitle()}
            </Typography>
          </Stack>
          <SideMenuMobile open={open} toggleDrawer={toggleDrawer} />
        </Stack>
      </Toolbar>
    </AppBar>
  );
}

export function CustomIcon() {
  return (
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: '10px',
        background: `linear-gradient(135deg, ${warmSuccessPrimary[600]} 0%, ${warmSuccessPrimary[700]} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: `0 3px 10px ${alpha(warmSuccessPrimary[600], 0.3)}`,
        flexShrink: 0,
      }}
    >
      <Typography
        sx={{
          color: '#fff',
          fontWeight: 800,
          fontSize: '1rem',
          fontFamily: '"DM Serif Display", Georgia, serif',
          lineHeight: 1,
        }}
      >
        F
      </Typography>
    </Box>
  );
}
